import { useCallback, useContext, useEffect, useState } from "react";
import {
  DeviceSettings,
  VideoPreview,
  useCall,
} from "@stream-io/video-react-sdk";
import Toast from "../Sweetalert/Sweetalert";
import { AuthContext } from "../../hooks/context/Context";
import { useSelector } from "react-redux";
import { API } from "../../shared/api/Api";
import axios from "axios";
import { selectUserInformations } from "../../features/authSlice";
import Loader from "./Loader";
import { showToast } from "../../utils/utils";
import ToggleSwitch from "../reusable/ToggleSwitch";

const LiveSetup = ({
  setIsSetupComplete,
}: {
  setIsSetupComplete: (value: boolean) => void;
}) => {
  const [isMicCamToggled, setIsMicCamToggled] = useState(false);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [loader, setLoader] = useState(false);
  const { menuOpen } = useContext(AuthContext);
  const call = useCall();
  const user = useSelector(selectUserInformations);

  if (!call) {
    Toast.fire({
      text: "useStreamCall must be used within a StreamCall component.",
      icon: "error",
    });
    throw new Error(
      "useStreamCall must be used within a StreamCall component."
    );
  }

  useEffect(() => {
    if (isMicCamToggled) {
      call.camera.disable();
      call.microphone.disable();
    } else {
      call.camera.enable();
      call.microphone.enable();
    }

    if (!call?.state.callingState) {
      console.error("Call is not ready for VideoPreview");
    }
  }, [isMicCamToggled, call.camera, call.microphone]);

  const joinStudent = useCallback(async () => {
    if (user.instructor && !call.isCreatedByMe) {
      showToast("Forbidden", "only students can join this live", "error");
    }

    if (call.isCreatedByMe) {
      return;
    }

    const { data } = await axios.post(
      API.Courses.app_courses_live_joinLive,
      {
        call_cid: call.cid,
      },
      {
        headers: {
          Authorization: `Bearer ${user.token}`,
          lang: "en",
        },
      }
    );

    if (data.status === 200 && data.errorCode === 0) {
      showToast("Live", data.message, "success");
    } else {
      showToast("Oops , try again", data.message, "error");
    }
  }, []);

  const handleClick = async (retries = 3) => {
    setLoader(true);
    try {
      await call.join({ create: true });
      if (!user.isInstructor) {
        await joinStudent();
      }
      setIsSetupComplete(true);
    } catch (error) {
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return handleClick(retries - 1);
      }
      showToast("Error", "Failed to join session", "error");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const initializeDevices = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (error: any) {
        setDeviceError(error.message);
        showToast(
          "Device Error",
          "Failed to access camera or microphone",
          "error"
        );
      }
    };

    initializeDevices();
  }, []);

  return (
    <div
      className={`min-h-[calc(100vh-77px)] ${
        menuOpen ? "content" : ""
      } flex flex-col items-center justify-center p-4`}
      id="content"
    >
      <h1 className="text-2xl font-bold text-center mb-8">Setup</h1>

      <div className="w-[85%] max-w-4xl mb-6">
        <div className="w-full h-[50vh] text-center">
          <VideoPreview className="w-full h-full border-0 bg-[var(--input-bg)] text-[var(--Tre-color)]" />
        </div>

        <div className="flex flex-col gap-4 mt-6">
          <div className="flex items-center gap-2 justify-center">
            <DeviceSettings />
            <ToggleSwitch
              label={"Join with mic and camera off"}
              onChange={() => setIsMicCamToggled(!isMicCamToggled)}
              checked={isMicCamToggled}
              // isMicCamToggled ? "success" : "default"
            />
          </div>
        </div>
      </div>

      <div className="w-full flex justify-center mt-6">
        {loader ? (
          <Loader />
        ) : (
          <button
            // @ts-ignore
            onClick={handleClick}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
          >
            Join Meeting
          </button>
        )}
      </div>
    </div>
  );
};

export default LiveSetup;
